import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { StageBadge, OutcomeBadge, money } from "@/components/opportunities/shared";
import { CloseWonDialog, CloseLostDialog } from "@/components/opportunities/CloseDialogs";
import { formatDate } from "@/components/prospects/shared";
import { CircleNotch, Kanban, ListBullets, CurrencyDollar, User } from "@phosphor-icons/react";

const ANY = "__any__";

// Pure translation of filter state -> GET /api/opportunities query params.
function buildListParams(f) {
  const orUndefined = (v) => (v === ANY || !v ? undefined : v);
  return {
    q: f.q || undefined,
    stage_id: orUndefined(f.stageId),
    service_division_id: orUndefined(f.divisionId),
    owner_id: orUndefined(f.ownerId),
    close_from: f.closeFrom || undefined,
    close_to: f.closeTo || undefined,
    archived: f.showArchived ? "true" : undefined,
  };
}

async function fetchBoardInto(setRows, params) {
  try {
    const { data } = await api.get("/opportunities", { params });
    setRows(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

async function fetchBoardRefsInto(setRefs) {
  try {
    const [stages, divisions, users] = await Promise.all([
      api.get("/reference/pipeline-stages"), api.get("/reference/service-divisions"), api.get("/users/options"),
    ]);
    setRefs({ stages: stages.data, divisions: divisions.data, users: users.data });
  } catch (e) {
    toast.error("Pipeline options could not be loaded. Please refresh.");
  }
}

// Conventional (non-drag) stage control: open stages transition directly,
// closed stages route through the audited close workflow dialogs.
function StageSelect({ opp, stages, onTransition, onWon, onLost }) {
  const handle = (stageId) => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage || stage.id === opp.pipeline_stage_id) return;
    if (stage.name === "Closed - Won") return onWon(opp);
    if (stage.name === "Closed - Lost") return onLost(opp);
    onTransition(opp, stageId);
  };
  return (
    <Select value={opp.pipeline_stage_id} onValueChange={handle} disabled={opp.outcome !== "open"}>
      <SelectTrigger className="h-7 rounded-sm text-xs" data-testid={`stage-select-${opp.id}`}
        onClick={(e) => e.stopPropagation()}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function OppCard({ opp, stages, onTransition, onWon, onLost }) {
  const navigate = useNavigate();
  return (
    <div
      draggable={opp.outcome === "open"}
      onDragStart={(e) => e.dataTransfer.setData("text/opportunity-id", opp.id)}
      onClick={() => navigate(`/opportunities/${opp.id}`)}
      className="cursor-pointer space-y-2 rounded-sm border border-[#E4E4E7] bg-white p-3 shadow-sm transition-colors hover:border-[#032852]/40"
      data-testid={`opp-card-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-[#09090B]">{opp.name}</p>
        <OutcomeBadge outcome={opp.outcome} />
      </div>
      <p className="truncate text-xs text-[#6D6D70]">{opp.prospect_name}</p>
      <div className="flex items-center justify-between text-xs text-[#52525B]">
        <span className="inline-flex items-center gap-1 font-semibold text-[#032852]">
          <CurrencyDollar size={13} /> {money(opp.first_12_month_revenue)}
        </span>
        <span className="inline-flex items-center gap-1"><User size={13} /> {opp.owner_name}</span>
      </div>
      {opp.target_close_date && (
        <p className="text-[11px] text-[#6D6D70]">Target close {formatDate(opp.target_close_date)}</p>
      )}
      <StageSelect opp={opp} stages={stages} onTransition={onTransition} onWon={onWon} onLost={onLost} />
    </div>
  );
}

function BoardColumn({ stage, opps, stages, onDropCard, onTransition, onWon, onLost }) {
  const [over, setOver] = useState(false);
  const total = opps.reduce((s, o) => s + (o.first_12_month_revenue || 0), 0);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropCard(e.dataTransfer.getData("text/opportunity-id"), stage); }}
      className={`flex w-64 shrink-0 flex-col rounded-sm border ${over ? "border-[#569A1F] bg-[#569A1F]/5" : "border-[#E4E4E7] bg-[#F4F4F5]/60"}`}
      data-testid={`board-column-${stage.id}`}
    >
      <div className="border-b border-[#E4E4E7] px-3 py-2.5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-[#032852]">{stage.name}</p>
          <span className="rounded-sm bg-[#032852]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#032852]">{opps.length}</span>
        </div>
        <p className="mt-0.5 text-[10px] text-[#6D6D70]">{stage.probability}% · {money(total)}</p>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        {opps.map((o) => (
          <OppCard key={o.id} opp={o} stages={stages} onTransition={onTransition} onWon={onWon} onLost={onLost} />
        ))}
      </div>
    </div>
  );
}

// Early-return content switch (board | empty list | table) — no nested ternaries.
function OpportunitiesContent({ view, rows, refs, byStage, onDropCard, onTransition, onWon, onLost, onOpen }) {
  if (rows === null) {
    return <div className="flex justify-center py-16"><CircleNotch size={24} className="animate-spin text-[#032852]" /></div>;
  }
  if (view === "board") {
    return (
      <div className="flex h-full gap-3 overflow-x-auto pb-2" data-testid="pipeline-board">
        {refs.stages.map((s) => (
          <BoardColumn key={s.id} stage={s} opps={byStage(s.id)} stages={refs.stages}
            onDropCard={onDropCard} onTransition={onTransition} onWon={onWon} onLost={onLost} />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-sm border border-dashed border-[#E4E4E7] bg-white py-10 text-center text-sm text-[#6D6D70]" data-testid="no-opportunities">
        No opportunities match these filters.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-sm border border-[#E4E4E7] bg-white" data-testid="opportunities-table">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[#E4E4E7] text-[10px] font-bold uppercase tracking-wide text-[#6D6D70]">
            <th className="px-4 py-3">Opportunity</th>
            <th className="px-4 py-3">Organization</th>
            <th className="px-4 py-3">Stage</th>
            <th className="px-4 py-3">12-Mo Value</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Target close</th>
            <th className="px-4 py-3">Change stage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} onClick={() => onOpen(o.id)}
              className="cursor-pointer border-b border-[#F4F4F5] last:border-0 hover:bg-[#F4F4F5]/60"
              data-testid={`opp-row-${o.id}`}>
              <td className="px-4 py-3 font-semibold text-[#09090B]">{o.name} <OutcomeBadge outcome={o.outcome} /></td>
              <td className="px-4 py-3 text-[#52525B]">{o.prospect_name}</td>
              <td className="px-4 py-3"><StageBadge name={o.stage_name} /></td>
              <td className="px-4 py-3 font-semibold text-[#032852]">{money(o.first_12_month_revenue)}</td>
              <td className="px-4 py-3 text-[#52525B]">{o.owner_name}</td>
              <td className="px-4 py-3 text-[#52525B]">{formatDate(o.target_close_date)}</td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <StageSelect opp={o} stages={refs.stages} onTransition={onTransition} onWon={onWon} onLost={onLost} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Opportunities() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("board"); // board | list
  const [rows, setRows] = useState(null);
  const [refs, setRefs] = useState({ stages: [], divisions: [], users: [] });
  const [q, setQ] = useState("");
  const [stageId, setStageId] = useState(ANY);
  const [divisionId, setDivisionId] = useState(ANY);
  const [ownerId, setOwnerId] = useState(ANY);
  const [closeFrom, setCloseFrom] = useState("");
  const [closeTo, setCloseTo] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [wonOf, setWonOf] = useState(null);
  const [lostOf, setLostOf] = useState(null);

  // buildListParams is module-level (referentially stable) and is bridged through
  // the same stable `helpers` ref used across this codebase, so the memo's
  // dependency list is complete without suppressing any hook rule.
  const helpers = useRef({ fetchBoardInto, fetchBoardRefsInto, buildListParams });
  const params = useMemo(
    () => helpers.current.buildListParams({ q, stageId, divisionId, ownerId, closeFrom, closeTo, showArchived }),
    [helpers, q, stageId, divisionId, ownerId, closeFrom, closeTo, showArchived]
  );

  const load = useCallback(() => helpers.current.fetchBoardInto(setRows, params), [helpers, setRows, params]);
  useEffect(() => { load(); }, [load]);
  const loadRefs = useCallback(() => helpers.current.fetchBoardRefsInto(setRefs), [helpers, setRefs]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const transition = async (opp, newStageId) => {
    try {
      await api.post(`/opportunities/${opp.id}/transition`, { stage_id: newStageId, version: opp.version });
      toast.success("Stage updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onDropCard = (oppId, stage) => {
    const opp = (rows || []).find((o) => o.id === oppId);
    if (!opp || stage.id === opp.pipeline_stage_id) return;
    if (opp.outcome !== "open") return toast.error("Closed opportunities must be reopened by an administrator first");
    if (stage.name === "Closed - Won") return setWonOf(opp);
    if (stage.name === "Closed - Lost") return setLostOf(opp);
    transition(opp, stage.id);
  };

  const byStage = (sid) => (rows || []).filter((o) => o.pipeline_stage_id === sid);

  return (
    <div className="flex h-full flex-col p-6 sm:p-8" data-testid="opportunities-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sales</p>
          <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Opportunities</h1>
          <p className="mt-2 text-sm text-[#6D6D70]">
            The opportunity stage is the pipeline record — drag a card or use its stage control.
            New opportunities are created from within the organization (Prospect or Client record), keeping every deal linked to its real organization, contacts and properties.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button onClick={() => setView("board")} data-testid="view-board"
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide ${view === "board" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}>
          <Kanban size={14} /> Board
        </button>
        <button onClick={() => setView("list")} data-testid="view-list"
          className={`inline-flex items-center gap-1.5 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide ${view === "list" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}>
          <ListBullets size={14} /> List
        </button>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#52525B]">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="filter-archived" />
          Archived only
        </label>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opportunities…" className="rounded-sm sm:w-52" data-testid="opp-search" />
        {view === "list" && (
          <Select value={stageId} onValueChange={setStageId}>
            <SelectTrigger className="rounded-sm sm:w-48" data-testid="filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All stages</SelectItem>
              {refs.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={divisionId} onValueChange={setDivisionId}>
          <SelectTrigger className="rounded-sm sm:w-48" data-testid="filter-division"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All divisions</SelectItem>
            {refs.divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-opp-owner"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All owners</SelectItem>
              {refs.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <DatePicker value={closeFrom} onChange={setCloseFrom} placeholder="Close from" className="sm:w-40" testId="filter-close-from" />
        <DatePicker value={closeTo} onChange={setCloseTo} placeholder="Close to" className="sm:w-40" testId="filter-close-to" />
      </div>

      <div className="mt-4 min-h-0 flex-1">
        <OpportunitiesContent view={view} rows={rows} refs={refs} byStage={byStage}
          onDropCard={onDropCard} onTransition={transition} onWon={setWonOf} onLost={setLostOf}
          onOpen={(oppId) => navigate(`/opportunities/${oppId}`)} />
      </div>

      {wonOf && (
        <CloseWonDialog key={`won-${wonOf.id}`} open opportunity={wonOf} isAdmin={isAdmin}
          onClose={() => setWonOf(null)} onDone={load} />
      )}
      {lostOf && (
        <CloseLostDialog key={`lost-${lostOf.id}`} open opportunity={lostOf}
          onClose={() => setLostOf(null)} onDone={load} />
      )}
    </div>
  );
}
